/**
 * Mapovani odpovedi audit API -> `R3CaseFixture` (krok O5b).
 *
 * Puvodne zilo v `apps/web-admin/src/lib/dd-api.ts` (krok S3) a umel ho jen
 * prohlizec. Krok O5b potrebuje TENTYZ tvar i na serveru (export reportu se
 * generuje z aktualnich dat auditu), takze mapovani je tady — jednou, pro oba.
 * Kdyby zustalo v UI, mel by dokument jinou pravdu nez obrazovka.
 *
 * Vstupni typy jsou schvalne STRUKTURALNI (jen pole, ktera se ctou): server
 * predava `CaseDetailDto` z `@reos/duediligence`, prohlizec generovany typ z
 * `@reos/api-client`. Obe strany popisuji tentyz kontrakt, jen jinym
 * generatorem — struktura je tu proto jediny rozumny spolecny jmenovatel.
 *
 * Co API neposila, se NEDOMYSLI: archivni snapshot dukazu, skore NBA a
 * klasifikace zdroje zustavaji prazdne (viz komentare u jednotlivych mapovani).
 */
// ---- mapovani -------------------------------------------------------------
/**
 * fact_key prefix -> prezentacni domena. Zrcadli `DOMAIN_BY_PREFIX` z
 * exportniho skriptu fixture (`duediligence/src/scripts/r3/export-fixture.ts`);
 * API domenu u faktu neposila, protoze je to ciste prezentacni deleni.
 */
const DOMAIN_BY_PREFIX = {
    address: 'identity',
    parcel: 'identity',
    scope: 'identity',
    building: 'identity',
    geometry: 'geometry',
    access: 'geometry',
    planning: 'planning',
    utilities: 'utilities',
    environment: 'environment',
    market: 'market',
    valuation: 'market',
    future: 'future',
};
function factDomain(factKey) {
    const prefix = factKey.split('.', 1)[0] ?? '';
    return DOMAIN_BY_PREFIX[prefix] ?? 'identity';
}
/**
 * Trust karta faktu (`claims`) je samostatne pole odpovedi — parovani jde pres
 * `factId`. Fakt bez karty (superseded radek) zustava bez bandu: neodhaduje se
 * ze `confidence`, to je jiny udaj.
 */
function toFixtureFact(fact, claim) {
    return {
        id: fact.id,
        factKey: fact.factKey,
        domain: factDomain(fact.factKey),
        value: fact.value,
        unit: fact.unit,
        status: fact.status,
        confidence: fact.confidence,
        impactValue: fact.impactValue,
        impactFeasibility: fact.impactFeasibility,
        sourceDate: fact.sourceDate,
        evidenceIds: claim?.evidenceIds ?? (fact.evidenceId === null ? [] : [fact.evidenceId]),
        independentSources: claim?.independentSources ?? (fact.evidenceId === null ? 0 : 1),
        trustBand: claim?.band ?? null,
        trustIndex: claim?.index ?? null,
        aiInvolvement: claim?.aiInvolvement ?? [],
        nodeId: fact.nodeId,
        derivedFrom: fact.derivedFrom,
    };
}
function toFixtureQuestion(question) {
    return {
        id: question.id,
        questionKey: question.questionKey,
        text: question.text,
        whyItMatters: question.whyItMatters,
        impact: question.impact,
        owner: question.owner,
        status: question.status,
        resolutionMode: question.resolutionMode,
        answeredByFactId: question.answeredByFactId,
        stopReason: question.stopReason,
        stopNote: question.stopNote,
        closableBy: question.closableBy,
    };
}
/** Archivni snapshot a klasifikace zdroje (capability/discovery/access) API neposila. */
function toFixtureEvidence(evidence) {
    return {
        id: evidence.id,
        sourceAdapter: evidence.sourceAdapter,
        url: evidence.requestUrl,
        fetchedAt: evidence.fetchedAt,
        httpStatus: evidence.httpStatus,
        mime: evidence.mime,
        capabilityClass: null,
        discoveryPathClass: null,
        accessClass: null,
        archive: null,
        licenseNote: evidence.licenseNote,
    };
}
/**
 * Dalsi nejlepsi kroky. API vraci cockpit tvar (`rank`, `title`, `why`,
 * `impact`, `blocking`), fixture mela skorovany tvar z `scoreNba` — ten se
 * pocita nad YAML konfiguraci na serveru a API ho neposila (Nalezy S3).
 *
 * ROZHODNUTI S3: `score` proto nese **poradi** (`rank`, 1 = nejdriv), ne
 * skore; karta NBA v detailu podle nej radi vzestupne a popisuje ho jako
 * poradi. `narrowsBandByPct`/`closesGateId` zustavaji null — o kolik krok
 * zuzi cenove pasmo, API u akce nerika.
 */
function toNbaItem(action) {
    return {
        questionId: action.questionId ?? '',
        questionKey: '',
        text: action.title,
        status: action.blocking ? 'BLOCKING' : 'OPEN',
        score: action.rank,
        components: {},
        action: action.why,
        resolutionMode: action.automationLevel,
        nextSource: null,
        narrowsBandByPct: null,
        closesGateId: null,
    };
}
/**
 * Beh auditu z research logu + podklady na vstupu. Pocty akci, stazeni a
 * selhani se pocitaji z logu — nic navic se nedopocitava: archivni snapshoty a
 * AI kroky po modelech API neposila, takze zustavaji nulove/prazdne.
 */
function toResearch(timeline, run, inputs) {
    const byKind = new Map();
    const bySource = new Map();
    const failures = [];
    let fetchTotal = 0;
    let fetchFailed = 0;
    for (const step of timeline) {
        byKind.set(step.actionKind, (byKind.get(step.actionKind) ?? 0) + 1);
        const failed = step.error !== null;
        if (step.url !== null) {
            fetchTotal += 1;
            if (failed)
                fetchFailed += 1;
        }
        if (step.sourceId !== null) {
            const bucket = bySource.get(step.sourceId) ?? { count: 0, failed: 0 };
            bucket.count += 1;
            if (failed)
                bucket.failed += 1;
            bySource.set(step.sourceId, bucket);
        }
        if (failed) {
            failures.push({
                actionKind: step.actionKind,
                sourceId: step.sourceId,
                reason: step.error,
                alternativeTried: null,
                url: step.url,
            });
        }
    }
    const startedAt = run?.startedAt ?? timeline[0]?.at ?? null;
    const finishedAt = run?.finishedAt ?? null;
    const durationMinutes = startedAt !== null && finishedAt !== null
        ? Math.max(0, Math.round((Date.parse(finishedAt) - Date.parse(startedAt)) / 60_000))
        : null;
    return {
        startedAt,
        finishedAt,
        durationMinutes,
        actionKinds: [...byKind.entries()].map(([kind, count]) => ({ kind, count })),
        fetch: { total: fetchTotal, ok: fetchTotal - fetchFailed, failed: fetchFailed },
        sources: [...bySource.entries()].map(([sourceId, s]) => ({
            sourceId,
            family: sourceId,
            count: s.count,
            failed: s.failed,
        })),
        failures,
        archive: { discovered: 0, fetched: 0, snapshots: [] },
        ai: { steps: byKind.get('ai_task') ?? 0, byTask: [] },
        inputs: inputs.map((input) => ({
            filename: input.filename,
            mime: input.mime,
            note: input.note,
            uploadedAt: input.uploadedAt,
        })),
    };
}
/**
 * Slozeni fixture z odpovedi API. Brany bere z `/detail` (plny `GateState`),
 * cenove pasmo a pripravenost z Trust vrstvy (`deal.get` v prohlizeci,
 * `assessCaseTrust` na serveru) — obe strany posilaji tyz kontrakt.
 */
export function fixtureFromApi(input) {
    const { detail } = input;
    const research = toResearch(input.timeline, input.run, input.inputs);
    const claimByFact = new Map(detail.claims.map((claim) => [claim.factId, claim]));
    return {
        generated: {
            exportedAt: research.finishedAt ?? research.startedAt ?? detail.case.openedAt,
            policyVersion: '',
            source: input.source ?? 'api',
        },
        caseInfo: {
            id: detail.case.id,
            kind: detail.case.kind,
            addressQuery: detail.case.addressQuery,
            status: detail.case.status,
            openedAt: detail.case.openedAt,
            closedAt: detail.case.closedAt,
        },
        questions: detail.questions.map(toFixtureQuestion),
        facts: detail.facts.map((fact) => toFixtureFact(fact, claimByFact.get(fact.id))),
        evidence: detail.evidence.map(toFixtureEvidence),
        nodes: detail.nodes.map((node) => ({
            id: node.id,
            nodeType: node.nodeType,
            nodeLabel: node.label,
            externalRef: node.externalRef,
        })),
        relations: detail.relations.map((relation) => ({
            id: relation.id,
            fromNodeId: relation.fromNodeId,
            toNodeId: relation.toNodeId,
            relationType: relation.relationType,
            properties: relation.properties,
            evidenceId: relation.evidenceId,
        })),
        gates: detail.gates,
        valuationBand: input.priceBand,
        nba: detail.nextBestActions.map(toNbaItem),
        trust: {
            policyVersion: '',
            caseKind: detail.case.kind,
            revision: null,
            computedAt: research.finishedAt,
            decisionReadiness: input.decisionReadiness,
            storedSummary: null,
            storedBands: null,
        },
        checklist: detail.checklist,
        research,
        sitePlan: detail.sitePlan,
    };
}
//# sourceMappingURL=from-api.js.map